<template>
  <ul
    class="pagination rs-pagination"
    role="menubar"
    aria-disabled="false"
    aria-label="Pagination"
  >
    <li
      role="presentation"
      :aria-hidden="currentPage <= 1"
      :class="['page-item', currentPage <= 1 ? 'disabled' : '']"
    >
      <a
        class="page-link"
        href="#"
        role="menuitem"
        aria-label="Go to first page"
        :aria-disabled="currentPage <= 1"
        @click.prevent="goToPage(1)"
      >
        <RsIcon icon="chevron-double-left" weight="far" />
      </a>
    </li>
    <li
      role="presentation"
      :aria-hidden="currentPage <= 1"
      :class="['page-item', currentPage <= 1 ? 'disabled' : '']"
    >
      <a
        class="page-link"
        href="#"
        role="menuitem"
        aria-label="Go to previous page"
        :aria-disabled="currentPage <= 1"
        @click.prevent="goToPage(currentPage - 1)"
      >
        <RsIcon icon="chevron-left" weight="far" />
      </a>
    </li>
    <li
      v-for="(page, i) in visiblePages"
      :key="page"
      role="presentation"
      class="page-item page-item--normal"
      :class="{ active: page === currentPage }"
    >
      <a
        class="page-link"
        href="#"
        role="menuitemradio"
        :aria-label="`Go to page ${page}`"
        :aria-checked="page === currentPage ? 'true' : 'false'"
        :aria-posinset="i + 1"
        :aria-setsize="visiblePages.length"
        :tabindex="page === currentPage ? 0 : -1"
        @click.prevent="goToPage(page)"
      >{{ page }}</a>
    </li>
    <li
      role="presentation"
      :aria-hidden="currentPage >= totalPages"
      :class="['page-item', currentPage >= totalPages ? 'disabled' : '']"
    >
      <a
        class="page-link"
        href="#"
        role="menuitem"
        aria-label="Go to next page"
        :aria-disabled="currentPage >= totalPages"
        @click.prevent="goToPage(currentPage + 1)"
      >
        <RsIcon icon="chevron-right" weight="far" />
      </a>
    </li>
    <li
      role="presentation"
      :aria-hidden="currentPage >= totalPages"
      :class="['page-item', currentPage >= totalPages ? 'disabled' : '']"
    >
      <a
        class="page-link"
        href="#"
        role="menuitem"
        aria-label="Go to last page"
        :aria-disabled="currentPage >= totalPages"
        @click.prevent="goToPage(totalPages)"
      >
        <RsIcon icon="chevron-double-right" weight="far" />
      </a>
    </li>
  </ul>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IPageRange } from '@/shared';

export default defineComponent({
  name: 'RsPagination',

  props: {
    value: {
      type: Object as () => IPageRange,
      default: () => ({
        recordsPerPage: 20,
        totalPages: 0,
        currentPage: 1,
        totalRecords: 0,
      } as IPageRange),
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: Object as () => IPageRange,
      default: undefined,
    },
  },

  emits: ['input', 'update:modelValue'],

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): IPageRange {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    currentPage(): number {
      return this.boundValue.currentPage || 1;
    },
    totalPages(): number {
      // Minimum of 1 so empty lists still render an active page-"1" button (BV parity).
      if (!this.boundValue.recordsPerPage || !this.boundValue.totalRecords) return 1;
      return Math.ceil(this.boundValue.totalRecords / this.boundValue.recordsPerPage);
    },
    visiblePages(): number[] {
      const total = this.totalPages;
      const current = this.currentPage;
      const windowSize = 5;
      const half = Math.floor(windowSize / 2);
      let start = Math.max(1, current - half);
      const end = Math.min(total, start + windowSize - 1);
      if (end - start < windowSize - 1) start = Math.max(1, end - windowSize + 1);
      const pages: number[] = [];
      for (let i = start; i <= end; i += 1) pages.push(i);
      return pages;
    },
  },

  methods: {
    goToPage(page: number): void {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      const out = { ...this.boundValue, currentPage: page };
      this.$emit('update:modelValue', out);
      this.$emit('input', out);
    },
  },
});
</script>

<style lang="scss">
  @import '@/styles/_variables.scss';
  $gray: #8694B3;
  .rs-pagination{
    .page-item{
      margin: 0 0.5rem 0 0;
      .page-link{
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0;
        min-width: 32px;
        height: 32px;
        border-radius: 3px;
        border: none;
        color: $gray;
        background-color: rgba($gray, 0.1);
        transition: background-color 0.2s ease;
        &:hover{
          background-color: rgba($gray, 0.2);
        }
        &:focus{
          box-shadow: none;
        }
      }

      &.disabled{
        border-radius: 3px;
        background-color: rgba($gray, 0.1);

        .page-link {
          background-color: rgba($gray, 0.1);
        }
      }

      &--normal{
        .page-link{
          border-radius: 3px;
          background-color: rgba($gray, 0.1);
        }
        &.active{
          border-radius: 3px;
          .page-link{
            background-color: $secondary-color;
            border-color: $secondary-color;
            color: $white;
          }
        }
      }
    }
    .rs-icon{
      font-size: 12px;
      color: rgba($tertiary-muted, 0.4);
    }
  }
</style>
